import { filter, isEmpty, map } from 'lodash/fp'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { OrderSummaryBreakdownCard } from 'account/components/OrderSummaryBreakdownCard'
import { useGetHistoricalOrdersQuery } from 'api/ordersApi'
import ErrorBanner from 'errors/components/ErrorBanner'
import ErrorModal from 'errors/components/ErrorModal'
import { t } from 'localization'
import { Button } from 'shared/components/Button'
import TextDivider from 'shared/components/TextDivider'
import { AccountQA } from 'shared/dataAttributes'
import { fonts } from 'shared/lib'
import { HistoricalOrder } from 'shared/types/HistoricalOrder'

const TP = 'account.components.orderList'

const handleErrorSubmit = () => {
  window.location.reload()
}

const getGoatIssueStr = (orders: HistoricalOrder[]): string => {
  const ordersWithIssues = filter((order) => {
    return order.status === 'goat_issue'
  }, orders)
  if (isEmpty(ordersWithIssues)) {
    return ''
  }
  const mappedOrdersToStr = map(
    (order) =>
      t(`${TP}.getGoatIssueOrdersStr`, {
        defaultValue: 'order #{orderNumber}',
        orderNumber: order.number,
      }),
    ordersWithIssues,
  )
  // use comma instead of "and" here for ease of translation
  return mappedOrdersToStr.join(', ')
}

export const OrderList = () => {
  const [page, setPage] = useState(1)
  const { data, isError, isLoading } = useGetHistoricalOrdersQuery(page)
  const [orders, setOrders] = useState<HistoricalOrder[]>([])
  const totalOrders = data?.orders?.length || 0

  const handleClickLoadMore = async () => {
    setPage(page + 1)
  }

  useEffect(() => {
    if (data && data.orders) {
      setOrders((previousOrders) => {
        if (previousOrders?.[0]?.number === data.orders?.[0]?.number) {
          // After canceling an order, replace historical orders with refetched historical orders
          return data.orders
        }
        // Click load more, append next page of historical orders to current historical orders
        return [...previousOrders, ...data.orders]
      })
    }
  }, [data?.orders, data])

  if (isLoading) {
    return null
  }

  return (
    <>
      {totalOrders > 0 && (
        <>
          {!isEmpty(getGoatIssueStr(orders)) && (
            <ErrorBanner
              data-testid="order-issue-error-msg"
              message={t(`${TP}.issueWithYourOrder`, {
                defaultValue:
                  'There is an issue with your {orderNumber}, please view the issue for more details.',
                orderNumber: getGoatIssueStr(orders),
              })}
            />
          )}
          <NoBulletList>
            {orders.map((order) => (
              <OrderSummaryBreakdownCard
                data-testid="summary-card"
                key={order.number}
                order={order}
              />
            ))}
          </NoBulletList>

          {/*
            25 is the amount of total Orders the historical order endpoint returns to us
            each order also only contains 1 product so in actuality this we only receive
            25 products in total
          */}
          {totalOrders !== 0 && totalOrders % 25 === 0 && (
            <Center>
              <Button
                buttonType="primary1"
                type="submit"
                onClick={handleClickLoadMore}
                disabled={isLoading}
                dataTestid="load-more-button"
                data-qa={AccountQA.OrderLoadMoreButton}
              >
                {t(`${TP}.loadMore`, 'Load more')}
              </Button>
            </Center>
          )}
          {totalOrders > 25 && totalOrders % 25 !== 0 && (
            <Center>
              <EndOfList qaAttr={AccountQA.OrderEndOfListTextDivider}>
                {t(`${TP}.endOfList`, 'End of list')}
              </EndOfList>
            </Center>
          )}
        </>
      )}
      {!orders ||
        (totalOrders === 0 && (
          <NoOrders data-testid="no-orders" data-qa={AccountQA.MyOrdersEmptySection}>
            {t(`${TP}.youHaveNoOrders`, 'You have no orders.')}
          </NoOrders>
        ))}
      <ErrorModal
        titleText={t(`${TP}.loadingError`, 'Loading Error')}
        message={t(
          `${TP}.unableToGet`,
          'Unable to get your orders. Please reach out to support@flightclub.com',
        )}
        isOpen={isError}
        buttonText={t(`${TP}.retry`, 'Retry')}
        onSubmit={handleErrorSubmit}
      />
    </>
  )
}

const NoOrders = styled.span`
  ${fonts.BODY_TEXT}
`

const EndOfList = styled(TextDivider)`
  width: 100%;
  max-width: 325px;
`

const Center = styled.div`
  display: flex;
  justify-content: center;
`

const NoBulletList = styled.ul`
  list-style-type: none;
  padding: 0;
`
